import { GetServerSidePropsContext, Redirect } from 'next';

import { getDictionary, Lang } from '@lang/getDictionary';
import getLocale from '@lang/getLocale';
import { Locale } from '@lang/getLocale';
import {
  fallbackFeatureFlagSnapshot,
  type FeatureFlagSnapshot,
  parseFeatureFlagOverrides,
} from '@onark/observability';
import { getSdk, Sdk } from '@OnArk/sdk';
import { internalPathOr } from '@onark/shared/internalPath';
import { applyCourseBranding } from '@OnArk/utils/courseBranding';
import { checkInRedirect } from '@OnArk/utils/guestPath';
import { isUuid } from '@OnArk/utils/isUuid';
import { loginRedirectTo } from '@OnArk/utils/loginRedirect';
import { GraphQLClient } from 'graphql-request';
import Cookies from 'universal-cookie';

import { retryingSdkWrapper } from './retryingSdkWrapper';

/**
 * The flag snapshot every legacy page carries in its props.
 *
 * Resolved here, once, rather than per page: it is the only wrapper both guest
 * and admin Pages Router routes pass through, so one line covers the whole
 * pre-App-Router tree. Serializing it into props is also what keeps the client
 * honest — `pages/_app` feeds it to the same FeatureFlagProvider the App
 * Router uses, so a flagged surface renders identically on the server and after
 * hydration instead of flipping.
 *
 * Overrides ONLY, and never a PostHog lookup. The first reason is a hard
 * constraint: `graphql/index.ts` re-exports both this module and `withClient`,
 * and `withClient` is a component, so THIS FILE IS IN THE CLIENT BUNDLE.
 * Importing `server/featureFlags` here pulled `server/internalChurches` ->
 * `@onark/db` -> `pg` -> `tls` into the browser graph and failed `next build`
 * with "Can't resolve 'tls'". Anything server-only imported here does the same,
 * so keep this to `@onark/observability`, which is browser-safe by design.
 *
 * The second is that it costs nothing. The flags riding these props are kill
 * switches, and the env override is the deterministic half of that mechanism:
 * no network, nothing to time out, changed by a Railway variable edit plus the
 * restart it already triggers. It also means a guest's class page cannot gain
 * a dependency on PostHog latency. A flag that genuinely needs per-church
 * targeting should be resolved in an App Router layout through
 * `featureFlagsFor`, which is server-only and can afford the import, or, for
 * a Pages Router page, in that page's getServerSideProps through a dynamic
 * import of `server/featureFlags` and returned as `featureFlags` (kept over
 * this snapshot by the prop assembly below).
 */
const ENV_OVERRIDES = parseFeatureFlagOverrides(
  process.env.FEATURE_FLAGS
).overrides;

function flagsForProps(): FeatureFlagSnapshot {
  return { ...fallbackFeatureFlagSnapshot(), ...ENV_OVERRIDES };
}

export type Layout =
  | 'ADMIN'
  | 'ADMIN_NARROW'
  | 'ADMIN_BUILDER'
  | 'ADMIN_GRID'
  | 'GUEST'
  | 'PUBLIC'
  | 'AUTH'
  | 'ADMIN_NO_CHURCH'
  | 'ADMIN_AUTH_SHELL';

type MaybePromise<T> = T | Promise<T>;

// The shared context withAuth hands to each page's getPageProps.
type AuthContext = GetServerSidePropsContext & {
  client: Sdk;
  user?: WithAuthUser;
  checkIn?: WithAuthCheckIn;
  church?: WithAuthChurch;
  locale?: Locale;
  lang: Lang;
};

// What a page's own getPageProps returns (before withAuth fills in the shared
// church/checkIn/user/lang/locale).
type PageResult<R> =
  | {
      props: R & {
        church?: DeepPartial<WithAuthChurch>;
        checkIn?: DeepPartial<WithAuthCheckIn>;
        user?: DeepPartial<WithAuthUser>;
        locale?: Locale;
        lang?: Lang;
        layout: Layout;
        /**
         * A page that needs per-church targeting resolves its own snapshot in
         * getServerSideProps (server-only there, via a dynamic import of
         * `server/featureFlags`) and hands it over here; see
         * pages/admin/classes/[courseId]/runs.tsx. Absent, the env-only
         * snapshot below applies.
         */
        featureFlags?: FeatureFlagSnapshot;
      };
      redirect?: never;
      notFound?: never;
    }
  | { redirect: Redirect; props?: never; notFound?: never }
  | { notFound: true; props?: never; redirect?: never };

// The props branch of PageResult, used by the prop-assembly helpers.
type PagePropsBranch<R> = Extract<PageResult<R>, { props: object }>;

// The final result withAuth returns, with the shared context guaranteed.
type AuthedResult<R> =
  | {
      props: R & {
        church: Exclude<WithAuthChurch, undefined>;
        checkIn: Exclude<WithAuthCheckIn, undefined>;
        user: Exclude<WithAuthUser, undefined>;
        locale: Locale;
        lang: Lang;
        layout: Layout;
      };
      redirect?: never;
      notFound?: never;
    }
  | { redirect: Redirect; props?: never; notFound?: never }
  | { notFound: true; props?: never; redirect?: never };

type GuardResult =
  | { redirect: Redirect; notFound?: never }
  | { notFound: true; redirect?: never };

type WithAuth = <R>(
  getPageProps: (context: AuthContext) => MaybePromise<PageResult<R>>,
  privateRoute: PrivateRoute
) => (context: GetServerSidePropsContext) => Promise<AuthedResult<R>>;

// Persist a referral code from `?ref=...` so it survives the user-signup →
// create-account hop. Sent server-side because the landing pages are SSR'd and
// we don't want to depend on client hydration to capture this. 30-day window
// matches the time we'll wait for a referred church to actually subscribe.
function persistReferralCookie(
  ctx: GetServerSidePropsContext,
  cookies: Cookies
): void {
  const refParam = ctx.query.ref;

  if (typeof refParam === 'string' && refParam && !cookies.get('onark-ref')) {
    const safe = encodeURIComponent(refParam);

    ctx.res.setHeader(
      'Set-Cookie',
      // 90 days — matches the onark-attribution window, so a referral/affiliate
      // click still credits a signup that happens weeks later (a church buying
      // cycle is longer than the old 30-day window).
      `onark-ref=${safe}; Path=/; Max-Age=${60 * 60 * 24 * 90}; SameSite=Lax`
    );
  }
}

// Where to come back to after signing in. `resolvedUrl` first: on a client-side
// transition `req.url` is the `/_next/data/….json` address, not the page.
const returnToOf = (ctx: GetServerSidePropsContext): string =>
  ctx.resolvedUrl || ctx.req.url || '/';

// Guards that only need the request's tokens, run before any data is loaded.
function guardBeforeData(
  privateRoute: PrivateRoute,
  token: string,
  guestToken: string,
  ctx: GetServerSidePropsContext
): GuardResult | null {
  if (!token && (privateRoute === 'ADMIN' || privateRoute === 'SUPER')) {
    return {
      redirect: {
        destination: loginRedirectTo(returnToOf(ctx)),
        permanent: false,
      },
    };
  }

  if (!guestToken && privateRoute === 'GUEST') {
    // checkInRedirect encodes the params (or 404s scanner garbage) so the
    // Location header is always valid — same rules as the section page.
    return checkInRedirect(ctx.params);
  }

  return null;
}

// Guest routes load church + check-in from the guest token, or redirect to the
// course when it's missing. Non-guest routes return null (keep me()'s data).
async function loadGuestData(
  privateRoute: PrivateRoute,
  guestToken: string,
  client: Sdk,
  ctx: GetServerSidePropsContext
): Promise<
  | GuardResult
  | {
      church: Awaited<ReturnType<Sdk['guestChurch']>>['church'];
      checkIn: Awaited<ReturnType<Sdk['guestChurch']>>['checkIn'];
    }
  | null
> {
  const isGuestRoute =
    privateRoute === 'GUEST' || privateRoute === 'REDIRECT_GUEST';

  if (isGuestRoute && guestToken) {
    const sectionId = ctx?.params?.sectionId;

    // Same guard as the section page, which runs AFTER this query: a
    // `{hash}.css.map` resolved against a guest page, or a scanner probe,
    // otherwise reaches Prisma inside guestChurch's submissions filter as
    // "Error creating UUID" — a 500 in onark-frontend AND onark-api instead
    // of the 404 the page would have returned.
    if (sectionId !== undefined && !isUuid(sectionId)) {
      return { notFound: true };
    }

    const guestQ = await client.guestChurch({ sectionId });

    // The course being viewed may override the guest brand (e.g. the Welcome
    // Aboard example wearing a demo church's identity). Merge it onto the
    // church so every guest surface (theme, nav, favicon, video stage) reflects
    // it; no-op when the course has no override.
    return {
      checkIn: guestQ.checkIn,
      church: applyCourseBranding(guestQ.church, guestQ.courseBranding),
    };
  }

  if (isGuestRoute) {
    return checkInRedirect(ctx.params);
  }

  return null;
}

// Guards that need the loaded user/church, run after the data fetch.
function guardAfterData(
  privateRoute: PrivateRoute,
  user: WithAuthUser | undefined | null,
  church: WithAuthChurch | undefined | null,
  ctx: GetServerSidePropsContext,
  cookies: Cookies
): GuardResult | null {
  // if this is a public route and you are logged in, redirect to the admin.
  if (privateRoute === 'REDIRECT' && user) {
    return {
      redirect: {
        destination: internalPathOr(ctx.query.to, '/admin/classes'),
        permanent: false,
      },
    };
  }

  if (privateRoute === 'ADMIN' && !user) {
    cookies.set('onark-token', '', { path: '/', secure: true });

    return {
      redirect: {
        destination: loginRedirectTo(returnToOf(ctx)),
        permanent: false,
      },
    };
  }

  if (
    privateRoute === 'ADMIN' &&
    !church &&
    ctx.resolvedUrl !== '/admin/create-account'
  ) {
    return {
      redirect: { destination: '/admin/create-account', permanent: false },
    };
  }

  return null;
}

// Role-gated routes: super-only. Split out from guardAfterData to keep each
// guard group under the complexity ceiling.
function guardRoleAccess(
  privateRoute: PrivateRoute,
  user: WithAuthUser | undefined | null
): GuardResult | null {
  if (privateRoute === 'SUPER' && !user?.super) {
    return { redirect: { destination: '/admin/classes', permanent: false } };
  }

  return null;
}

// Fill the shared context onto the page's getPageProps argument.
function buildPageContext(
  base: AuthContext,
  church: WithAuthChurch | undefined | null,
  checkIn: WithAuthCheckIn | undefined | null,
  user: WithAuthUser | undefined | null
): AuthContext {
  return {
    ...base,
    checkIn: checkIn ?? ({} as WithAuthCheckIn),
    church: church ?? ({} as WithAuthChurch),
    user: user ?? ({} as WithAuthUser),
  };
}

// Unauthenticated render path: pass the page's own props through with the empty
// church/checkIn/user the layout expects, without loading any account data.
function renderPublic<R>(
  pageProps: PageResult<R>,
  lang: Lang,
  locale: Locale
): AuthedResult<R> {
  if (pageProps.redirect !== undefined || pageProps.notFound !== undefined) {
    return pageProps;
  }

  return {
    ...pageProps,
    props: {
      ...pageProps.props,
      checkIn: { ...(pageProps.props.checkIn ?? {}) } as WithAuthCheckIn,
      church: { ...(pageProps.props.church ?? {}) } as WithAuthChurch,
      featureFlags: pageProps.props.featureFlags ?? flagsForProps(),
      lang,
      layout: pageProps.props.layout,
      locale,
      user: { ...(pageProps.props.user ?? {}) } as WithAuthUser,
    },
  };
}

// Authenticated render path: merge the loaded church/checkIn/user (and billing
// summary) under the page's own props.
function buildAuthedProps<R>(
  pageProps: PagePropsBranch<R>,
  ctx: {
    billing: { plan?: string | null; status?: string | null }[];
    church: WithAuthChurch | undefined | null;
    checkIn: WithAuthCheckIn | undefined | null;
    user: WithAuthUser | undefined | null;
    lang: Lang;
    locale: Locale;
  }
): AuthedResult<R> {
  return {
    props: {
      ...pageProps.props,
      // Pass billing summary through so LayoutWrapper can gate the onboarding
      // widget for paying customers without a second query.
      billing: ctx.billing,
      checkIn: {
        ...ctx.checkIn,
        ...(pageProps.props.checkIn ?? {}),
      } as WithAuthCheckIn,
      church: {
        ...ctx.church,
        ...(pageProps.props.church ?? {}),
      } as WithAuthChurch,
      featureFlags: pageProps.props.featureFlags ?? flagsForProps(),
      lang: ctx.lang,
      layout: pageProps.props.layout,
      locale: ctx.locale,
      user: {
        ...ctx.user,
        ...(pageProps.props.user ?? {}),
      } as WithAuthUser,
    },
  };
}

const withAuth: WithAuth = (getPageProps, privateRoute = 'PUBLIC') => {
  return async ctx => {
    const { cookie, referer } = ctx.req.headers;
    const cookies = new Cookies(cookie);
    const token = cookies.get('onark-token') as string;
    const guestToken = cookies.get('onark-guest-token') as string;

    persistReferralCookie(ctx, cookies);

    const client = new GraphQLClient(process.env.NEXT_PUBLIC_GRAPHQL_URI!, {
      headers: { 'from-url': referer! },
      mode: 'cors',
    });
    const locale = getLocale(ctx.req);
    const lang = await getDictionary(locale);

    if (token) client.setHeader('authorization', token);
    if (guestToken) client.setHeader('authorization-guest', guestToken);
    const ContextWithClient: AuthContext = {
      ...ctx,
      // Every server-side query goes through the retry wrapper, so a Cloudflare
      // origin blip on api.onark.app costs a few hundred milliseconds instead of
      // the whole page render. Queries only — see retryingSdkWrapper.
      client: getSdk(client, retryingSdkWrapper),
      lang,
      locale,
    };

    const preGuard = guardBeforeData(privateRoute, token, guestToken, ctx);

    if (preGuard) return preGuard;

    // if there is no auth then just render the page and don't worry about
    // getting user/church/checkin data
    if (!token && !guestToken) {
      return renderPublic(await getPageProps(ContextWithClient), lang, locale);
    }

    let church: WithAuthChurch | undefined | null;
    let user: WithAuthUser | undefined | null;
    let checkIn: WithAuthCheckIn | undefined | null;
    let billing: { plan?: string | null; status?: string | null }[] = [];

    if (token) {
      const me = await ContextWithClient.client.me();

      church = me.church;
      user = me.user;
      billing = me.billing || [];
    }

    const guestData = await loadGuestData(
      privateRoute,
      guestToken,
      ContextWithClient.client,
      ctx
    );

    if (guestData && !('church' in guestData)) return guestData;
    if (guestData) {
      church = guestData.church;
      checkIn = guestData.checkIn;
    }

    const postGuard = guardAfterData(privateRoute, user, church, ctx, cookies);

    if (postGuard) return postGuard;

    const roleGuard = guardRoleAccess(privateRoute, user);

    if (roleGuard) return roleGuard;

    const pageProps = await getPageProps(
      buildPageContext(ContextWithClient, church, checkIn, user)
    );

    if (pageProps.redirect !== undefined || pageProps.notFound !== undefined) {
      return pageProps;
    }

    return buildAuthedProps(pageProps, {
      billing,
      checkIn,
      church,
      lang,
      locale,
      user,
    });
  };
};

export default withAuth;
